import {getEq} from 'fp-ts/lib/Array';
import {eqBoolean, eqString, getStructEq} from 'fp-ts/lib/Eq';
import {ActiveAgentNumberStatus, AgentNumberStatus, AgentNumberStatusId} from 'Shared/constants/AgentNumberStatus';
import {ToMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';

export class UnvalidatedAgentNumber {
  public readonly key = 'UnvalidatedAgentNumber';

  constructor(
    public readonly value: string,
    public readonly isNewYork: boolean,
    public readonly status: AgentNumberStatus = ActiveAgentNumberStatus
  ) {}
}

export class InvalidAgentNumber {
  public static create(agentNumber: string, isNewYork: boolean, status: AgentNumberStatus = ActiveAgentNumberStatus) {
    return agentNumber === ''
      ? new UnvalidatedAgentNumber('', isNewYork, status)
      : new InvalidAgentNumber(agentNumber, isNewYork, status);
  }

  public readonly key = 'InvalidAgentNumber';

  private constructor(
    public readonly value: string,
    public readonly isNewYork: boolean,
    public readonly status: AgentNumberStatus = ActiveAgentNumberStatus
  ) {}
}

export class ValidAgentNumber {
  public static create(agentNumber: string, isNewYork: boolean, status: AgentNumberStatus = ActiveAgentNumberStatus) {
    return agentNumber.length === 5
      ? new ValidAgentNumber(agentNumber, isNewYork, status)
      : InvalidAgentNumber.create(agentNumber, isNewYork, status);
  }

  public readonly key = 'ValidAgentNumber';

  private constructor(
    public readonly value: string,
    public readonly isNewYork: boolean,
    public readonly status: AgentNumberStatus = ActiveAgentNumberStatus
  ) {}
}

export type AgentNumber = InvalidAgentNumber | ValidAgentNumber | UnvalidatedAgentNumber;

type AgentNumberOptions<TReturn> = ToMatchOptions<AgentNumber, 'key', TReturn>;

export const matchAgentNumber = <TReturn>(agentNumber: AgentNumber, options: AgentNumberOptions<TReturn>) => {
  switch (agentNumber.key) {
    case 'InvalidAgentNumber':
      return options.InvalidAgentNumber(agentNumber);
    case 'UnvalidatedAgentNumber':
      return options.UnvalidatedAgentNumber(agentNumber);
    case 'ValidAgentNumber':
      return options.ValidAgentNumber(agentNumber);
  }
};

export const eqAgentNumber = getStructEq({
  value: eqString,
  isNewYork: eqBoolean,
});

export const eqArrayOfAgentNumbers = getEq(eqAgentNumber);

export const isAgentNumberInStatus = (status: AgentNumberStatusId) => (agentNumber: AgentNumber) =>
  agentNumber.status.id === status;
